/**
 * Badges de vérification (ADR 0012 « contrat UI », ADR 0014 §6) : `content/factcheck.status.json`, calculé au build depuis le contenu
 * et content/factcheck.json (même inventaire, même hash que la CI). Un chiffre absent de la table n'a jamais le badge « vérifié ».
 */
import { escapeHtml as e } from "./format.js";
import { icon } from "./icons.js";

export type FcBadge = "verifie" | "officielle" | "une_source" | "ecart" | "a_verifier";
export type FcTable = Record<string, { b: FcBadge; c?: string }>;

/**
 * Libellés de l'identité MAP (IDENTITE.md §7 « Badge vérifié ») branchés sur les statuts du fact-checker (ADR 0018) :
 * « vérifié » seulement pour `verifie` et `officielle`, qui sont des verdicts du fact-checker ; une source ou chiffre modifié
 * depuis le verdict → « en cours de vérification » / « à vérifier » ; écart relevé → « contesté ».
 */
const LABELS: Record<FcBadge, string> = {
  verifie: "vérifié · 2 sources",
  officielle: "vérifié · source officielle",
  une_source: "1 source · en cours de vérification",
  ecart: "contesté",
  a_verifier: "à vérifier",
};

/** Le fact-checker a relu le chiffre et l'a confirmé. */
export const FC_VERIFIED: ReadonlySet<FcBadge> = new Set<FcBadge>(["verifie", "officielle"]);

let table: FcTable | null = null;

export function parseFactcheckStatus(raw: unknown): FcTable | null {
  if (!raw || typeof raw !== "object") return null;
  const f = (raw as { figures?: unknown }).figures;
  if (!f || typeof f !== "object") return null;
  const out: FcTable = {};
  for (const [id, v] of Object.entries(f as Record<string, unknown>)) {
    if (!v || typeof v !== "object") continue;
    const b = (v as { b?: unknown }).b;
    if (typeof b !== "string" || !(b in LABELS)) continue;
    const c = (v as { c?: unknown }).c;
    out[id] = typeof c === "string" ? { b: b as FcBadge, c } : { b: b as FcBadge };
  }
  return out;
}

export function setFactcheck(t: FcTable | null): void {
  table = t;
}

/** Badge d'un chiffre ; null si le chiffre n'est pas sourcé (aucun badge de vérification, statut TODO existant). */
export function fcBadge(figureId: string | undefined): { badge: FcBadge; label: string; comment?: string } | null {
  if (!figureId || !table) return null;
  const v = table[figureId];
  if (!v) return null;
  return { badge: v.b, label: LABELS[v.b], ...(v.c ? { comment: v.c } : {}) };
}

/** Contenu du badge : icône `verifie` (vérifié), `source` (contesté) ou aucune, puis le statut en toutes lettres. */
export function fcBadgeInner(b: FcBadge, label: string): string {
  const ic = FC_VERIFIED.has(b) ? icon("verifie", 18) : b === "ecart" ? icon("source", 18) : "";
  return `${ic}<span>${e(label)}</span>`;
}

/**
 * Badge HTML (classe `fc fc-<badge>`), infobulle = commentaire du fact-checker pour un écart. Avec `srcId` (bouton ⓘ voisin),
 * le badge est lui-même un bouton qui ouvre le même panneau source (IDENTITE.md §7 : « toujours cliquable »).
 */
export function fcBadgeHtml(figureId: string | undefined, srcId?: string): string {
  const b = fcBadge(figureId);
  if (!b) return "";
  const attrs = `class="fc fc-${b.badge}" data-fc="${e(figureId ?? "")}"${b.comment ? ` title="${e(b.comment)}"` : ""}`;
  return srcId
    ? ` <button type="button" ${attrs} data-src="${e(srcId)}" aria-label="Statut de vérification : ${e(b.label)}. Voir les sources">${fcBadgeInner(b.badge, b.label)}</button>`
    : ` <span ${attrs}>${fcBadgeInner(b.badge, b.label)}</span>`;
}
